import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';

import { paths } from '@/paths';
import axios from '@/lib/v2/axios';

interface InvitationApiResponse {
  error: string | null;
  message: string;
  verified: boolean;
  can_resend?: boolean;
}

export default function Invitation(): React.JSX.Element {
  const { token } = useParams();
  const navigate = useNavigate();
  const [resendSuccess, setResendSuccess] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery<{ data: InvitationApiResponse }>({
    queryKey: ['invitation', token],
    queryFn: async () => await axios.post(`/api/v2/invitations/verify`, { token }),
    retry: false,
  });
  const invitationData = data?.data;

  const resendMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`/api/v2/invitations/resend-by-token`, { token });
      return response.data;
    },
    onSuccess: () => {
      setResendSuccess(true);
    },
  });

  // Use an effect to perform navigation rather than calling navigate directly in the render
  useEffect(() => {
    if (invitationData?.verified) {
      navigate(paths.auth.signIn, { replace: true });
    }
  }, [invitationData, navigate]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Alert severity="error">Unable to verify invitation</Alert>
      </Box>
    );
  }

  if (invitationData?.error) {
    const isExpired = invitationData.error === 'expired';
    const canResend = invitationData.can_resend;
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {invitationData.message}
        </Alert>
        
        {resendSuccess ? <Alert severity="success" sx={{ mb: 2 }}>
            A new invitation has been sent to your email. Please check your inbox for the new invitation link.
          </Alert> : null}
        
        {isExpired && canResend && !resendSuccess ? <Box sx={{ textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
              Would you like us to send you a new invitation?
            </Typography>
            <Button
              disabled={resendMutation.isPending}
              onClick={() => { resendMutation.mutate(); }}
              variant="contained"
            >
              {resendMutation.isPending ? 'Sending...' : 'Send New Invitation'}
            </Button>
          </Box> : null}
        
        {invitationData.error === 'already_accepted' && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
              You can now sign in to your account.
            </Typography>
            <Button
              onClick={() => { navigate(paths.auth.signIn); }}
              variant="contained"
            >
              Go to Sign In
            </Button>
          </Box>
        )}
        
        {invitationData.error === 'not_found' && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">
              Please check your invitation link or contact your administrator.
            </Typography>
          </Box>
        )}
        
        {resendMutation.isError ? <Alert severity="error" sx={{ mt: 2 }}>
            Failed to send new invitation. Please try again or contact support.
          </Alert> : null}
      </Box>
    );
  }

  // If invitation is verified, we don't need to render anything since we're redirecting.
  if (invitationData?.verified) {
    return <></>;
  }

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}
    >
      <Alert severity="success">Account Verified</Alert>
    </Box>
  );
}
